import React, { useState, useEffect, useRef } from "react";
import LiveFeedUtils from "Pages/Dashboard/dashboard/LiveFeed/utils";
import { months } from "Pages/Dashboard/dashboard/LiveFeed/constants";

export const formatLiDate = value => {
  if (!value) return "";
  const date = new Date(value);
  const mon = months[date.getMonth()];
  const hours = ((date.getHours() + 11) % 12) + 1;
  const mins = date.getMinutes();
  const meridiem = date.getHours() >= 12 ? "PM" : "AM";
  const hh = hours === 12 && meridiem === "AM" ? "00" : hours;
  const mm = mins > 9 ? mins : `0${mins}`;
  if (LiveFeedUtils.isYesterday(date)) return `Yesterday - ${hh}:${mm} ${meridiem}`;
  if (LiveFeedUtils.isToday(date)) return `Today - ${hh}:${mm} ${meridiem}`;
  return `${mon} ${date.getDate()}, ${date.getFullYear()} - ${hh}:${mm} ${meridiem}`;
};

export const OwnerChip = ({
  ownerName = "",
  ownerProfileUrl = "",
  profilePictureUrl = ""
}) => {
  const [broken, setBroken] = useState(false);
  return (
    <span className="inboxV2LiSender">
      <span className="inboxV2LiSenderPic">
        {profilePictureUrl && !broken ? (
          <img src={profilePictureUrl} alt="" onError={() => setBroken(true)} />
        ) : (
          <span className="material-icons">account_circle</span>
        )}
      </span>
      <a href={ownerProfileUrl} target="_blank" rel="noopener noreferrer">
        {ownerName}
      </a>
    </span>
  );
};

const LiAvatar = ({ imageUrl = "" }) => {
  const [broken, setBroken] = useState(false);
  return (
    <span className="inboxV2LiBubbleAvatar">
      {imageUrl && !broken ? (
        <img src={imageUrl} alt="" onError={() => setBroken(true)} />
      ) : (
        <span className="material-icons">account_circle</span>
      )}
    </span>
  );
};

// Presentational LinkedIn chat: bubble list (oldest -> newest), owner banner, and a
// scroll-to-latest button. Shared by the Inbox V2 thread and the LinkedIn reply popup;
// each surface owns its own composer. `messages` come newest-first (API order).
const LinkedInChat = ({
  messages = [],
  loading = false,
  canViewLiReplies = true,
  liConnected = true,
  isOwner = false,
  ownerName = "",
  ownerProfileUrl = "",
  profilePictureUrl = "",
  step = ""
}) => {
  const chatRef = useRef(null);
  const lastMsgRef = useRef(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const orderedMessages = [...messages].reverse();

  const scrollToLatest = (smooth = false) => {
    const el = chatRef.current;
    const last = lastMsgRef.current;
    if (!el || !last) return;
    const top =
      el.scrollTop +
      last.getBoundingClientRect().top -
      el.getBoundingClientRect().top -
      12;
    el.scrollTo({ top, behavior: smooth ? "smooth" : "auto" });
  };

  useEffect(() => {
    scrollToLatest();
  }, [messages.length, loading]);

  const onScroll = () => {
    const el = chatRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 40;
    setShowScrollBtn(!atBottom);
  };

  return (
    <>
      <div className="inboxV2LiChat" ref={chatRef} onScroll={onScroll}>
        {loading ? (
          <div className="inboxV2EditorLoading inboxV2LiLoading">
            <span className="inboxV2Spinner" />
            <span>Loading conversation...</span>
          </div>
        ) : !canViewLiReplies ? (
          <div className="inboxV2LiPermission">
            You don’t have permission to view this LinkedIn conversation. Only the
            owner of the connected LinkedIn account can access replies.
          </div>
        ) : (
          <>
            {liConnected && !isOwner && ownerName && (
              <div className="inboxV2LiInfo inboxV2LiInfoCenter">
                You're viewing a LinkedIn conversation received on:{" "}
                <OwnerChip
                  ownerName={ownerName}
                  ownerProfileUrl={ownerProfileUrl}
                  profilePictureUrl={profilePictureUrl}
                />
                .
              </div>
            )}
            {step && (
              <div className="inboxV2LiStepRow">
                <span className="inboxV2Pill">{step}</span>
              </div>
            )}
            {orderedMessages.map((item, index) => {
              const {
                senderName = "",
                updateTs = "",
                imageUrl = "",
                message = "",
                date = ""
              } = item || {};
              const msgDate = date || updateTs;
              const isOut =
                !!ownerName &&
                (senderName || "").trim().toLowerCase() ===
                  ownerName.trim().toLowerCase();
              return (
                <div
                  className={`inboxV2LiMessage ${isOut ? "out" : "in"}`}
                  key={`liMsg${index}`}
                  ref={
                    index === orderedMessages.length - 1 ? lastMsgRef : null
                  }
                >
                  <LiAvatar imageUrl={isOut ? profilePictureUrl : imageUrl} />
                  <div className="inboxV2LiBubbleWrap">
                    <div className="inboxV2LiBubble">{message}</div>
                    <div className="inboxV2LiDate">{formatLiDate(msgDate)}</div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {showScrollBtn && (
        <button
          type="button"
          className="inboxV2LiScrollDown"
          onClick={() => scrollToLatest(true)}
        >
          <i className="material-icons">keyboard_arrow_down</i>
          Latest messages
        </button>
      )}
    </>
  );
};

export default LinkedInChat;
